import React from "react";
import Icons from "@/components/Icons";
import { Checkbox } from "@/components/ui/checkbox";
import type { PaymentMethodCardListProps } from "@/interfaces";
import type { IconType } from "@/types";
import type { ThemeConfig } from "@/lib/theme-utils";

const PaymentMethodCardList = ({
  savedCards,
  selectedCardId,
  lastSelectedCardId,
  isLoading,
  showCardDropdown,
  setShowCardDropdown,
  handleSavedCardSelect,
  theme,
}: PaymentMethodCardListProps & { theme?: ThemeConfig }) => {
  const primaryColor = theme?.colors?.primary || '#49BDF3';
  // Filter out PayPal methods - only show actual cards in the cards list
  const creditCards = savedCards.filter(card => !card.id.startsWith('paypal_vaulted_'));
  
  if (creditCards.length > 1) {
    const mainCard = creditCards.find(
      (card) => card.id === selectedCardId || card.id === lastSelectedCardId
    );

    const handleCardClick = () => {
      if (!selectedCardId) {
        const cardId = lastSelectedCardId || creditCards[0]?.id;
        if (cardId) handleSavedCardSelect(cardId, true);
      }
    };

    return (
      <div className="relative mb-3">
        <div
          role="button"
          tabIndex={0}
          className={`w-full text-left flex justify-between items-center h-[30px] pr-5 py-5 pl-3.5 bg-[#F7F7F7] focus:outline-none cursor-pointer ${
            isLoading ? "opacity-60 pointer-events-none" : ""
          }`}
          onClick={() => {
            handleCardClick();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleCardClick();
            }
          }}
          style={{ background: "", borderTop: `1px solid ${primaryColor}`, borderBottom: `1px solid ${primaryColor}` }}
        >
          <div className="flex items-center gap-[7px]">
            <div className="h-[29px] w-[40px] rounded-[3px] flex items-center justify-center">
              <Icons
                type={
                  (mainCard?.card?.brand as IconType) || ("card" as IconType)
                }
                size={46}
              />
            </div>
            <div>
              <h3 className="text-[16px] font-normal leading-none flex items-center gap-2" style={{ color: primaryColor }}>
                {mainCard?.card?.brand
                  ? `**** - ${mainCard.card.last4}`
                  : "Card"}
                <button
                  className="ml-2 inline-block align-middle rotate-90 focus:outline-none"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCardDropdown(!showCardDropdown);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.stopPropagation();
                      setShowCardDropdown(!showCardDropdown);
                    }
                  }}
                  disabled={isLoading}
                  aria-label="Toggle card dropdown"
                >
                  <Icons type="arrow" size={10} />
                </button>
              </h3>
            </div>
          </div>

          <Checkbox
            aria-label="Select card for payment"
            checked={!!selectedCardId}
            onCheckedChange={() => {
              handleCardClick();
            }}
            disabled={isLoading}
          />
        </div>
        {showCardDropdown && (
          <div className="absolute z-10 left-0 right-0 bg-white border rounded shadow mt-1 max-h-60 overflow-y-auto">
            {creditCards.map((card) => (
              <div
                key={card.id}
                role="button"
                tabIndex={0}
                className={`w-full text-left flex items-center justify-between gap-[7px] pl-3 pr-5 py-2 hover:bg-gray-100 ${
                  selectedCardId === card.id ? "bg-gray-100" : ""
                }`}
                onClick={() => {
                  handleSavedCardSelect(card.id, true);
                  setShowCardDropdown(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleSavedCardSelect(card.id, true);
                    setShowCardDropdown(false);
                  }
                }}
                style={{ cursor: isLoading ? "not-allowed" : "pointer" }}
                aria-disabled={isLoading}
              >
                <div className="flex items-center gap-[7px]">
                  <div className="h-[29px] w-[40px] rounded-[3px] flex items-center justify-center">
                    <Icons
                      type={(card.card.brand as IconType) || ("card" as IconType)}
                      size={46}
                      />
                  </div>

                  <span className="text-[16px] text-[#3d392c] font-normal leading-none">
                    **** - {card.card.last4}
                  </span>
                </div>

                <Checkbox
                  aria-label="Select this card for payment"
                  checked={selectedCardId === card.id}
                  onCheckedChange={(checked: boolean) =>
                    handleSavedCardSelect(card.id, checked)
                  }
                  disabled={isLoading}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (creditCards.length === 1) {
    const card = creditCards[0];
    
    const handleCardClick = () => {
      if (!selectedCardId) {
        handleSavedCardSelect(card.id, true);
      }
    };

    return (
      <div className="relative mb-3">
        <div
          role="button"
          tabIndex={0}
          className={`w-full text-left flex justify-between items-center h-[30px] pr-5 py-5 pl-3.5 bg-[#F7F7F7] focus:outline-none cursor-pointer ${
            isLoading ? "opacity-60 pointer-events-none" : ""
          }`}
          onClick={handleCardClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleCardClick();
            }
          }}
          style={{ background: "", borderTop: `1px solid ${primaryColor}`, borderBottom: `1px solid ${primaryColor}` }}
        >
          <div className="flex items-center gap-[7px]">
            <div className="h-[29px] w-[40px] rounded-[3px] flex items-center justify-center">
              <Icons
                type={(card.card.brand as IconType) || ("card" as IconType)}
                size={46}
              />
            </div>
            <div>
              <h3 className="text-[16px] font-normal leading-none" style={{ color: primaryColor }}>
                {card.card.brand
                  ? `**** - ${card.card.last4}`
                  : "Card"}
                {/* No dropdown arrow for single card */}
              </h3>
            </div>
          </div>

          <Checkbox
            aria-label="Select card for payment"
            checked={!!selectedCardId}
            onCheckedChange={handleCardClick}
            disabled={isLoading}
          />
        </div>
      </div>
    );
  }
  return null;
};

export default React.memo(PaymentMethodCardList);
